import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';
import { useBewerber, useUploadDocument, useDeleteDocument, useDownloadDocument, useMarkBewerber, useUnmarkBewerber, useUpdateBewerber } from '../../api/bewerber';
import { UserSelector } from '../../components/UserSelector';
import { BewerberStatusBadge } from '../../components/bewerber/BewerberStatusBadge';
import { StatusActionButtons } from '../../components/bewerber/StatusActionButtons';
import { OnboardingChecklist } from '../../components/bewerber/OnboardingChecklist';
import { OffboardingChecklist } from '../../components/bewerber/OffboardingChecklist';
import { VorstellungsgespraechCard } from '../../components/bewerber/vorstellungsgespraech/VorstellungsgespraechCard';
import { BedarfeInReichweiteSection } from '../../components/bewerber/vorstellungsgespraech/BedarfeInReichweiteSection';
import { ActivityTimeline } from '../../components/activity';
import { DocumentListItem } from '../../components/document';
import { ActionDropdown } from '../../components/common/ActionDropdown';
import { AbsageModal } from '../../components/bewerber/AbsageModal';
import { NichtGeeignetModal } from '../../components/bewerber/NichtGeeignetModal';
import { HardDeleteModal } from '../../components/bewerber/HardDeleteModal';
import {
  BewerberStatus,
  DokumentTyp,
  DokumentTypConfig,
  MATCHING_ELIGIBLE_STATUSES,
  hasAnyOffboardingStep,
} from '../../types/bewerber';
import { VG_ALLOWED_STATUSES } from '../../types/vorstellungsgespraech';
import { Gender, GenderConfig } from '../../types/bedarf';
import { formatAddressLines } from '../../utils/formatAddress';

/** German labels for the Fazit "Geeignet für" values stored on the Bewerber. */
const GEEIGNET_FUER_LABELS: Record<string, string> = {
  koerperlich: 'Körperlich',
  seelisch: 'Seelisch',
  pflegerisch: 'Pflegerisch',
};

export function BewerberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data, isLoading, error } = useBewerber(id!);
  const uploadMutation = useUploadDocument();
  const deleteDocMutation = useDeleteDocument();
  const downloadMutation = useDownloadDocument();
  const markMutation = useMarkBewerber();
  const unmarkMutation = useUnmarkBewerber();
  const updateMutation = useUpdateBewerber();

  const [showUpload, setShowUpload] = useState(false);
  const [uploadType, setUploadType] = useState<DokumentTyp>(DokumentTyp.LEBENSLAUF);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAbsageModalOpen, setIsAbsageModalOpen] = useState(false);
  const [isNichtGeeignetModalOpen, setIsNichtGeeignetModalOpen] = useState(false);
  const [isHardDeleteModalOpen, setIsHardDeleteModalOpen] = useState(false);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !id) return;

    try {
      await uploadMutation.mutateAsync({
        bewerberId: id,
        file: selectedFile,
        typ: uploadType,
      });
      toast.success('Dokument erfolgreich hochgeladen');
      setShowUpload(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error('Fehler beim Hochladen des Dokuments');
    }
  };

  const handleDeleteDocument = async (documentId: string, filename: string) => {
    const confirmed = await confirm({
      title: 'Dokument löschen',
      message: `Möchten Sie "${filename}" wirklich löschen?`,
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await deleteDocMutation.mutateAsync({
          bewerberId: id!,
          documentId,
        });
        toast.success('Dokument erfolgreich gelöscht');
      } catch (error) {
        console.error('Failed to delete document:', error);
        toast.error('Fehler beim Löschen des Dokuments');
      }
    }
  };

  const handleDownload = async (documentId: string, filename: string) => {
    try {
      await downloadMutation.mutateAsync({
        bewerberId: id!,
        documentId,
        filename,
      });
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Fehler beim Herunterladen des Dokuments');
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-500">Laden...</p>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Fehler beim Laden des Bewerbers</p>
        <Link to="/bewerber" className="text-primary-600 hover:text-primary-900 mt-2 inline-block">
          Zurück zur Liste
        </Link>
      </div>
    );
  }

  const bewerber = data.data;
  // The FK lives on the Bedarf, so this arrives as a list. Fachlich there is at
  // most one — BedarfeService refuses a second — hence the first entry.
  const zugeordneterBedarf = bewerber.zugeordneteBedarfe?.[0] ?? null;
  const addressLines = formatAddressLines(
    bewerber.street,
    bewerber.postalCode,
    bewerber.city?.name,
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={() => {
            const listUrl = sessionStorage.getItem('bewerber-list-url');
            navigate(listUrl || '/bewerber');
          }}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center mb-3"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zurück zur Liste
        </button>

        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center">
            <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center mr-3">
              <span className="text-primary-600 font-medium text-lg">
                {bewerber.firstName[0]}{bewerber.lastName[0]}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900">
                  {bewerber.firstName} {bewerber.lastName}
                </h1>
                {/* Visual markers */}
                {bewerber.isReopened && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700"
                    title="Reaktiviert"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reaktiviert
                  </span>
                )}
                {bewerber.hasSecondApplication && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700"
                    title="Zweite Bewerbung"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Zweite Bewerbung
                  </span>
                )}
                {/* Confirmation status */}
                {bewerber.isConfirmed ? (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-700"
                    title={bewerber.confirmedAt ? `Bestätigt am ${new Date(bewerber.confirmedAt).toLocaleString('de-DE')}` : 'Bestätigt'}
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Bestätigt
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700"
                    title="Bestätigungsmail wurde noch nicht gesendet"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Nicht bestätigt
                  </span>
                )}
                {/* Attention marker */}
                {bewerber.isMarked && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700"
                    title={bewerber.markedAt ? `Markiert am ${new Date(bewerber.markedAt).toLocaleString('de-DE')}` : 'Markiert'}
                  >
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4 4v17l6-3 6 3V4H4zm0-2h12a2 2 0 012 2v17.586a1 1 0 01-1.707.707L10 18.586l-6.293 3.707A1 1 0 012 21.586V4a2 2 0 012-2z" />
                    </svg>
                    Markiert
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center">
                <BewerberStatusBadge status={bewerber.status} />
                <span className="ml-2 text-sm text-gray-500 group relative cursor-help">
                  Erstellt am {new Date(bewerber.createdAt).toLocaleDateString('de-DE')}
                  <span className="absolute left-0 top-full mt-1 hidden group-hover:block z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg">
                    <span className="block">Erstellt von: {bewerber.createdBy ? `${bewerber.createdBy.firstName} ${bewerber.createdBy.lastName}` : '-'}</span>
                    <span className="block">Aktualisiert: {new Date(bewerber.updatedAt).toLocaleString('de-DE')}</span>
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3 sm:mt-0 flex items-center space-x-2">
            <UserSelector
              id="assignedTo"
              value={bewerber.assignedToId}
              onChange={async (userId) => {
                try {
                  await updateMutation.mutateAsync({
                    id: bewerber.id,
                    data: { assignedToId: userId },
                  });
                  toast.success(userId ? 'Zuweisung aktualisiert' : 'Zuweisung entfernt');
                } catch {
                  toast.error('Fehler beim Aktualisieren der Zuweisung');
                }
              }}
              disabled={updateMutation.isPending}
              className="w-44"
            />
            <Link
              to={`/bewerber/${bewerber.id}/bearbeiten`}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Bearbeiten
            </Link>
            {/* Action dropdown for Aktionen */}
            <ActionDropdown
              label="Aktionen"
              variant="danger"
              items={[
                // Mark/Unmark option
                {
                  label: bewerber.isMarked ? 'Markierung entfernen' : 'Markieren',
                  onClick: async () => {
                    try {
                      if (bewerber.isMarked) {
                        await unmarkMutation.mutateAsync(bewerber.id);
                        toast.success('Markierung entfernt');
                      } else {
                        await markMutation.mutateAsync(bewerber.id);
                        toast.success('Bewerber markiert');
                      }
                    } catch (error) {
                      toast.error('Fehler beim Ändern der Markierung');
                    }
                  },
                },
                // Show Nicht geeignet for statuses except VERLOREN, GEWONNEN, NICHT_GEEIGNET
                ...(bewerber.status !== BewerberStatus.VERLOREN &&
                   bewerber.status !== BewerberStatus.GEWONNEN &&
                   bewerber.status !== BewerberStatus.OFFBOARDING &&
                   bewerber.status !== BewerberStatus.NICHT_GEEIGNET ? [{
                  label: 'Nicht geeignet',
                  onClick: () => setIsNichtGeeignetModalOpen(true),
                }] : []),
                // Only show Absagen for non-VERLOREN and non-GEWONNEN Bewerber.
                // OFFBOARDING has its own way out ("Offboarding abschließen").
                ...(bewerber.status !== BewerberStatus.VERLOREN &&
                   bewerber.status !== BewerberStatus.GEWONNEN &&
                   bewerber.status !== BewerberStatus.OFFBOARDING ? [{
                  label: 'Absagen',
                  onClick: () => setIsAbsageModalOpen(true),
                }] : []),
                {
                  label: 'Permanent löschen',
                  onClick: () => setIsHardDeleteModalOpen(true),
                },
              ]}
            />
          </div>
        </div>

        <StatusActionButtons bewerber={bewerber} />
      </div>

      {/*
        Einstellen-Checkliste. In beiden Status bedienbar: in ONBOARDING treibt
        sie den Vorgang (beide Haken schieben auf GEWONNEN), in GEWONNEN wird
        sie weitergeführt — ein Schritt lässt sich dort noch nachtragen.
      */}
      {(bewerber.status === BewerberStatus.ONBOARDING || bewerber.status === BewerberStatus.GEWONNEN) && (
        <div className="mb-4">
          <OnboardingChecklist bewerber={bewerber} />
        </div>
      )}

      {/*
        Offboarding: the wind-up list is the task, the onboarding record below it
        the context — folded away, since it is only ever read here. On VERLOREN
        the list survives as a record, but only if it was ever used: an ordinary
        Absage never touched it and would show nine empty rows for nothing.
      */}
      {bewerber.status === BewerberStatus.OFFBOARDING && (
        <>
          <div className="mb-4">
            <OffboardingChecklist bewerber={bewerber} />
          </div>
          <div className="mb-4">
            <OnboardingChecklist bewerber={bewerber} collapsible />
          </div>
        </>
      )}
      {bewerber.status === BewerberStatus.VERLOREN && hasAnyOffboardingStep(bewerber) && (
        <div className="mb-4">
          <OffboardingChecklist bewerber={bewerber} />
        </div>
      )}

      {/* Interview history (renders itself away when there is none yet) */}
      {VG_ALLOWED_STATUSES.includes(bewerber.status) && (
        <div className="mb-4">
          <VorstellungsgespraechCard bewerber={bewerber} />
        </div>
      )}

      {/* Unified Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Personal Data */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Persönliche Daten</h3>
          </div>
          <div className="p-4">
            <dl className="space-y-2">
              <div>
                <dt className="text-xs font-medium text-gray-500">E-Mail</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.email ? (
                    <a href={`mailto:${bewerber.email}`} className="text-primary-600 hover:text-primary-900">
                      {bewerber.email}
                    </a>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Telefon</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.phone ? (
                    <a href={`tel:${bewerber.phone}`} className="text-primary-600 hover:text-primary-900">
                      {bewerber.phone}
                    </a>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Geschlecht</dt>
                <dd className="text-sm text-gray-900">{bewerber.gender ? GenderConfig[bewerber.gender as Gender]?.label || bewerber.gender : '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Geburtsdatum</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.dateOfBirth ? new Date(bewerber.dateOfBirth).toLocaleDateString('de-DE') : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Adresse</dt>
                <dd className="text-sm text-gray-900">
                  {addressLines.length > 0
                    ? addressLines.map((line) => <div key={line}>{line}</div>)
                    : '-'}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Qualification & Education */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Qualifikation & Ausbildung</h3>
          </div>
          <div className="p-4">
            <dl className="space-y-2">
              <div>
                <dt className="text-xs font-medium text-gray-500">Fachkraft</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.isFachkraft === true ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ja</span>
                  ) : bewerber.isFachkraft === false ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Nein</span>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Erfahrung mit Kindern</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.erfahrungMitKindern === true ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ja</span>
                  ) : bewerber.erfahrungMitKindern === false ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Nein</span>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Bildungsabschluss</dt>
                <dd className="text-sm text-gray-900">{bewerber.bildungsabschluss || '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Ausbildungsdauer</dt>
                <dd className="text-sm text-gray-900">{bewerber.ausbildungsDauer || '-'}</dd>
              </div>
              {bewerber.berufserfahrung && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Berufserfahrung</dt>
                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{bewerber.berufserfahrung}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Documents - spans 2 columns on xl */}
        <div className="bg-white shadow rounded-lg xl:col-span-1">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-base font-medium text-gray-900">Dokumente</h3>
            <button
              onClick={() => setShowUpload(!showUpload)}
              className="text-sm text-primary-600 hover:text-primary-900"
            >
              {showUpload ? 'Abbrechen' : '+ Hochladen'}
            </button>
          </div>
          <div className="p-4">
            {showUpload && (
              <form onSubmit={handleUpload} className="mb-3 p-3 bg-gray-50 rounded-lg">
                <div className="mb-2">
                  <label htmlFor="docType" className="block text-xs font-medium text-gray-700 mb-1">
                    Dokumenttyp
                  </label>
                  <select
                    id="docType"
                    value={uploadType}
                    onChange={(e) => setUploadType(e.target.value as DokumentTyp)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                  >
                    {Object.entries(DokumentTypConfig).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-2">
                  <label htmlFor="file" className="block text-xs font-medium text-gray-700 mb-1">
                    Datei
                  </label>
                  <input
                    type="file"
                    id="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                    className="block w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                </div>
                <button
                  type="submit"
                  disabled={!selectedFile || uploadMutation.isPending}
                  className="w-full inline-flex justify-center items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadMutation.isPending ? 'Wird hochgeladen...' : 'Hochladen'}
                </button>
              </form>
            )}
            {bewerber.dokumente && bewerber.dokumente.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {bewerber.dokumente.map((doc) => (
                  <DocumentListItem
                    key={doc.id}
                    document={doc}
                    bewerberId={bewerber.id}
                    onDownload={handleDownload}
                    onDelete={handleDeleteDocument}
                    isDownloading={downloadMutation.isPending}
                    isDeleting={deleteDocMutation.isPending}
                  />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 text-center py-2">Keine Dokumente</p>
            )}
          </div>
        </div>

        {/* Availability & Compensation */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Verfügbarkeit & Vergütung</h3>
          </div>
          <div className="p-4">
            <dl className="space-y-2">
              <div>
                <dt className="text-xs font-medium text-gray-500">Verfügbar ab</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.availableFrom ? new Date(bewerber.availableFrom).toLocaleDateString('de-DE') : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Kündigungsfrist</dt>
                <dd className="text-sm text-gray-900">{bewerber.kuendigungsfrist || '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Ort der Stelle</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.ortStellen && bewerber.ortStellen.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {bewerber.ortStellen.map((s) => (
                        <span
                          key={s.id}
                          className="inline-flex items-center rounded-full bg-primary-50 text-primary-700 text-xs px-2 py-0.5"
                        >
                          {s.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Stelle</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.beworbenAufBedarf ? (
                    <Link
                      to={`/bedarfe/${bewerber.beworbenAufBedarf.id}`}
                      className="text-primary-600 hover:text-primary-500"
                    >
                      {[
                        bewerber.beworbenAufBedarf.childFirstName,
                        bewerber.beworbenAufBedarf.childLastName,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    </Link>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
              {/* The counterpart to "Stelle" one row up: that one is the
                  application, this one the position actually worked. Rendered
                  only when there is one, so an unassigned Bewerber does not
                  carry an empty row next to a filled one. */}
              {zugeordneterBedarf && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Eingesetzt bei</dt>
                  <dd className="text-sm text-gray-900">
                    <Link
                      to={`/bedarfe/${zugeordneterBedarf.id}`}
                      className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5 hover:bg-emerald-100"
                      title={
                        zugeordneterBedarf.schoolName
                          ? `Schule: ${zugeordneterBedarf.schoolName}`
                          : undefined
                      }
                    >
                      {[
                        zugeordneterBedarf.childFirstName,
                        zugeordneterBedarf.childLastName,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    </Link>
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-medium text-gray-500">Wochenstunden</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.minWochenstunden != null || bewerber.maxWochenstunden != null
                    ? `${bewerber.minWochenstunden ?? '-'} - ${bewerber.maxWochenstunden ?? '-'} Std.`
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Geforderter Stundensatz</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.geforderterStundensatz != null ? `${Number(bewerber.geforderterStundensatz).toFixed(2)} €` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Angebotener Stundensatz</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.angebotenerStundensatz != null ? `${Number(bewerber.angebotenerStundensatz).toFixed(2)} €` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">TVöD SUE</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.tvoedsueLevel || bewerber.tvoedsueStufe != null
                    ? `${bewerber.tvoedsueLevel || '-'} / Stufe ${bewerber.tvoedsueStufe ?? '-'}`
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Geeignet für (Fazit)</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.geeignetFuer && bewerber.geeignetFuer.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {bewerber.geeignetFuer.map((v) => (
                        <span
                          key={v}
                          className="inline-flex items-center rounded-full bg-primary-50 text-primary-700 text-xs px-2 py-0.5"
                        >
                          {GEEIGNET_FUER_LABELS[v] ?? v}
                        </span>
                      ))}
                    </div>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Mobility */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Mobilität</h3>
          </div>
          <div className="p-4">
            <dl className="space-y-2">
              <div>
                <dt className="text-xs font-medium text-gray-500">Führerschein</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.hasFuehrerschein === true ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ja</span>
                  ) : bewerber.hasFuehrerschein === false ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Nein</span>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Auto vorhanden</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.hasAuto === true ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ja</span>
                  ) : bewerber.hasAuto === false ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Nein</span>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">ÖPNV</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.hasOepnv === true ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ja</span>
                  ) : bewerber.hasOepnv === false ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Nein</span>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Max. Pendelzeit</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.maxPendelMinuten != null ? `${bewerber.maxPendelMinuten} Min.` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Max. Pendelstrecke</dt>
                <dd className="text-sm text-gray-900">
                  {bewerber.maxPendelKm != null ? `${bewerber.maxPendelKm} km` : '-'}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/*
          Bedarfe in Reichweite — the same list the Vorstellungsgespräch shows,
          judged here against the stored columns instead of live interview
          answers (the section falls back to them on its own).

          Only for statuses that take part in matching at all: everyone else has
          no coordinates and never will, so the list could only ever say "wird
          noch verortet".
        */}
        {MATCHING_ELIGIBLE_STATUSES.includes(bewerber.status) && (
          <div className="bg-white shadow rounded-lg flex flex-col max-h-[28rem]">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-medium text-gray-900">Bedarfe in Reichweite</h3>
            </div>
            {/*
              The list scrolls inside the tile rather than stretching the page.
              min-h-0 is what actually permits that: a flex child refuses to
              shrink below its content without it, and the overflow would move
              to the page instead.
            */}
            <div className="min-h-0 overflow-y-auto p-4">
              <BedarfeInReichweiteSection bewerberId={bewerber.id} answers={{}} />
            </div>
          </div>
        )}

        {/* Notes */}
        {bewerber.notes && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-medium text-gray-900">Notizen</h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{bewerber.notes}</p>
            </div>
          </div>
        )}

        {/* Qualifications JSON */}
        {bewerber.qualifications && Object.keys(bewerber.qualifications).length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-medium text-gray-900">Qualifikationen</h3>
            </div>
            <div className="p-4">
              <dl className="space-y-2">
                {Object.entries(bewerber.qualifications).map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-xs font-medium text-gray-500 capitalize">{key}</dt>
                    <dd className="text-sm text-gray-900">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}

        {/* Activity Timeline - spans 2 columns on md+ */}
        <div className="md:col-span-2 xl:col-span-3">
          <ActivityTimeline bewerberId={bewerber.id} />
        </div>
      </div>

      {/* Modals */}
      <AbsageModal
        isOpen={isAbsageModalOpen}
        onClose={() => setIsAbsageModalOpen(false)}
        bewerber={bewerber}
        onSuccess={() => {
          setIsAbsageModalOpen(false);
          // Optionally navigate to list after absage
        }}
      />

      <NichtGeeignetModal
        isOpen={isNichtGeeignetModalOpen}
        onClose={() => setIsNichtGeeignetModalOpen(false)}
        bewerber={bewerber}
        onSuccess={() => {
          setIsNichtGeeignetModalOpen(false);
        }}
      />

      <HardDeleteModal
        isOpen={isHardDeleteModalOpen}
        onClose={() => setIsHardDeleteModalOpen(false)}
        bewerber={bewerber}
        onSuccess={() => {
          setIsHardDeleteModalOpen(false);
          navigate('/bewerber');
        }}
      />
    </div>
  );
}
